import { useAuthStore } from '../stores/authStore';
import { posthog, isPostHogConfigured } from '../lib/posthog';

// Supabase Edge Functions URL
// For local development, use: http://localhost:54321/functions/v1
// For production, use: https://your-project.supabase.co/functions/v1
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:54321/functions/v1';

// Supabase anon key - required for all Edge Function calls
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export type ErrorResponse = {
  success: false;
  error: {
    message: string;
    code?: string;
  };
};

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Get auth headers from the store
 * - If authenticated: sends Authorization Bearer token (user JWT)
 * - If anonymous: sends Authorization Bearer anon key + X-Anonymous-Id header
 * 
 * IMPORTANT: Supabase Edge Functions always require an Authorization header!
 */
const getAuthHeaders = (): Record<string, string> => {
  const state = useAuthStore.getState();
  const headers: Record<string, string> = {};

  // Always send Authorization header (user JWT or anon key)
  if (state.session?.access_token) {
    // User is authenticated - send user JWT
    headers['Authorization'] = `Bearer ${state.session.access_token}`;
  } else if (SUPABASE_ANON_KEY) {
    // Anonymous mode - send anon key
    headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
  }

  // Always send anonymous ID for linking purposes
  if (state.anonymousId) {
    headers['X-Anonymous-Id'] = state.anonymousId;
  }

  return headers;
};

export const customInstance = async <T>({
  url,
  method,
  params,
  data,
  headers: customHeaders,
  signal,
}: {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: Record<string, string | number | boolean | undefined>;
  data?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
}): Promise<T> => {
  const authHeaders = getAuthHeaders();

  const headers: Record<string, string> = {
    ...authHeaders,
  };

  const hasBody = data !== undefined && data !== null;
  const isFormData = hasBody && (data instanceof FormData || (data as { constructor?: { name?: string } })?.constructor?.name === 'FormData');
  if (hasBody && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  if (isFormData) {
    delete headers['Content-Type'];
  }

  if (customHeaders) {
    Object.entries(customHeaders as Record<string, string>).forEach(([key, value]) => {
      if (key.toLowerCase() === 'content-type' && (!hasBody || isFormData)) return;
      headers[key] = value;
    });
  }

  let finalUrl = `${API_URL}${url}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      finalUrl += `?${queryString}`;
    }
  }

  const config: RequestInit = {
    method,
    headers,
    signal,
  };

  if (data && method !== 'GET') {
    config.body = isFormData ? (data as FormData) : JSON.stringify(data);
  }

  try {
    const response = await fetch(finalUrl, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      
      if (errorData && !errorData.success) {
        const apiError = new ApiError(
          errorData.error.message || 'Request failed',
          response.status,
          errorData.error.code
        );
        
        // Track API error
        if (isPostHogConfigured()) {
          const eventName = response.status >= 500 ? 'api_error_5xx' : 'api_error_4xx';
          posthog.capture(eventName, {
            url: finalUrl,
            method,
            status: response.status,
            error: apiError.message,
            code: apiError.code,
          });
        }
        
        throw apiError;
      }

      const apiError = new ApiError(
        `Request failed with status ${response.status}`,
        response.status
      );
      
      // Track generic API error
      if (isPostHogConfigured()) {
        const eventName = response.status >= 500 ? 'api_error_5xx' : 'api_error_4xx';
        posthog.capture(eventName, {
          url: finalUrl,
          method,
          status: response.status,
          error: apiError.message,
        });
      }
      
      throw apiError;
    }

    const result = await response.json();
    return result as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      const networkError = new ApiError('Network error - please check your connection', 0);
      
      // Track network error
      if (isPostHogConfigured()) {
        posthog.capture('api_network_error', {
          url: finalUrl,
          method,
          error: networkError.message,
        });
      }
      
      throw networkError;
    }

    const unexpectedError = new ApiError('An unexpected error occurred', 500);
    
    // Track unexpected error
    if (isPostHogConfigured()) {
      posthog.capture('api_error_unexpected', {
        url: finalUrl,
        method,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
    
    throw unexpectedError;
  }
};

export default customInstance;
